import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { createPlatformApi } from '../utils/platformApi';
import { getOwnedSchoolIdForBilling, isOwnedFreelanceSchoolForBilling } from '../utils/activeSchool';
import ActiveStudentsModal from './ActiveStudentsModal';
import PlanChangeModal from './PlanChangeModal';
import PaymentHistoryModal from './PaymentHistoryModal';
import { useTranslation } from 'react-i18next';
import { formatDate, formatMonth } from '../utils/dateFormat';

const fmt = (n) => new Intl.NumberFormat('ru-RU').format(n);

export default function BillingDashboard() {
    const { t } = useTranslation(['platformBilling', 'common', 'nav', 'messages', 'errors']);
    const STATUS_LABELS = useMemo(() => ({
        active: { text: t('platformBilling:statusActive'), color: '#27ae60' },
        trial: { text: t('platformBilling:statusTrial'), color: '#2980b9' },
        past_due: { text: t('platformBilling:statusPastDue'), color: '#e67e22' },
        blocked: { text: t('platformBilling:statusBlocked'), color: '#c0392b' }
    }), [t]);
    const CHARGE_STATUS = useMemo(() => ({
        paid: { label: t('platformBilling:chargePaid'), color: '#27ae60' },
        pending: { label: t('platformBilling:chargePending'), color: '#f39c12' },
        invoiced: { label: t('platformBilling:chargeInvoiced'), color: '#f39c12' },
        failed: { label: t('platformBilling:chargeFailed'), color: '#c0392b' },
        zero: { label: t('platformBilling:chargeZero'), color: '#95a5a6' }
    }), [t]);
    const PAYMENT_STATUS = useMemo(() => ({
        succeeded: { label: t('platformBilling:paymentStatus_succeeded'), color: '#27ae60' },
        pending: { label: t('platformBilling:paymentStatus_pending'), color: '#f39c12' },
        canceled: { label: t('platformBilling:paymentStatus_canceled'), color: '#95a5a6' },
        failed: { label: t('platformBilling:paymentStatus_failed'), color: '#c0392b' },
    }), [t]);
    const api = createPlatformApi();
    const [plan, setPlan] = useState(null);
    const [charges, setCharges] = useState([]);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [noOwnedSchool, setNoOwnedSchool] = useState(false);
    const [showStudents, setShowStudents] = useState(false);
    const [showPlanChange, setShowPlanChange] = useState(false);
    const [showPaymentHistory, setShowPaymentHistory] = useState(false);

    const isFreelanceSchool = plan?.isFreelanceSchool === true || isOwnedFreelanceSchoolForBilling();

    useEffect(() => { load(); }, []);

    async function load() {
        setLoading(true);
        setNoOwnedSchool(false);
        const schoolId = getOwnedSchoolIdForBilling();
        if (!schoolId) {
            setPlan(null);
            setCharges([]);
            setHistory([]);
            setNoOwnedSchool(true);
            setLoading(false);
            return;
        }
        const params = { schoolId };
        try {
            const [p, c, h] = await Promise.all([
                api.get('/platform/billing/current-plan', { params }),
                api.get('/platform/billing/extra-charges', { params }),
                api.get('/platform/billing/history', { params })
            ]);
            setPlan(p.data);
            setCharges(c.data || []);
            setHistory(h.data || []);
        } catch {
            setPlan(null);
        }
        setLoading(false);
    }

    if (loading) return <p style={{ padding: '24px' }}>{t('platformBilling:loading')}</p>;

    if (noOwnedSchool) {
        return (
            <section style={card}>
                <h3 style={h3}>{t('platformBilling:noOwnedSchoolTitle')}</h3>
                <p style={{ color: '#64748b', lineHeight: 1.55, marginBottom: 16 }}>
                    {t('platformBilling:noOwnedSchoolBody')}
                </p>
                <Link to="/school" style={btn}>{t('platformBilling:createSchoolCta')}</Link>
            </section>
        );
    }

    if (!plan) return <p style={{ padding: '24px' }}>{t('platformBilling:loadError')}</p>;

    const st = STATUS_LABELS[plan.platformStatus] || STATUS_LABELS.trial;
    const base = plan.baseStudentLimit || 0;
    const current = plan.currentActiveStudents || 0;
    const pct = base > 0 ? Math.min(100, Math.round((current / base) * 100)) : 0;
    const over = plan.extraStudents > 0;
    const schoolsPct = plan.maxSchools >= 999999 ? 0
        : Math.min(100, Math.round((plan.schoolsCount / plan.maxSchools) * 100));
    const nextBill = plan.nextBillingDate
        ? formatDate(plan.nextBillingDate, { day: 'numeric', month: 'long' })
        : '—';
    const historyPreview = history.slice(0, 5);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700 }}>
                {isFreelanceSchool
                    ? t('platformBilling:freelanceTitle')
                    : t('platformBilling:schoolTitle')}
            </h2>

            <section style={card}>
                <h3 style={h3}>{t('platformBilling:currentPlan')}</h3>
                <p style={{ margin: '4px 0' }}>
                    <strong>{plan.plan?.displayName}</strong>
                    <span style={{ marginLeft: '12px', color: st.color, fontWeight: 600 }}>{st.text}</span>
                </p>
                {plan.plan?.hasCustomPricing && (
                    <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#b45309' }}>
                        {t('platformBilling:customPricing', { price: fmt(plan.plan?.monthlyPrice) })}
                        {plan.plan?.catalogMonthlyPrice != null && plan.plan.catalogMonthlyPrice !== plan.plan.monthlyPrice && (
                            <>{t('platformBilling:catalogPrice', { price: fmt(plan.plan.catalogMonthlyPrice) })}</>
                        )}
                    </p>
                )}
                <p style={{ color: '#666', fontSize: '14px' }}>
                    {t('platformBilling:nextBilling', { date: nextBill })}
                    {plan.accessExpiresAt && (
                        <>{t('platformBilling:accessUntil', { date: formatDate(plan.accessExpiresAt) })}</>
                    )}
                </p>
                <div style={{ display: 'flex', gap: '10px', marginTop: '12px', flexWrap: 'wrap' }}>
                    <button type="button" onClick={() => setShowPlanChange(true)} style={btn}>{t('platformBilling:changePlan')}</button>
                    <Link to="/billing/plans" style={{ ...btn, textDecoration: 'none' }}>{t('platformBilling:allPlans')}</Link>
                </div>
            </section>

            <section style={card}>
                <h3 style={h3}>{t('platformBilling:students')}</h3>
                <div style={{ background: '#e5e7eb', borderRadius: '8px', height: '12px', overflow: 'hidden' }}>
                    <div style={{
                        width: `${pct}%`, height: '100%',
                        background: over ? '#ef4444' : '#4f46e5',
                        transition: 'width 0.3s'
                    }} />
                </div>
                <p style={{ margin: '8px 0', fontSize: '15px' }}>
                    {t('platformBilling:studentsOfBase', { current, base })}
                    {over && (
                        <span style={{ color: '#c0392b', display: 'block', marginTop: '6px' }}>
                            {t('platformBilling:extraStudents', { count: plan.extraStudents, amount: fmt(plan.estimatedExtraCharge) })}
                            {plan.nextBillingDate && t('platformBilling:billingFirst')}
                        </span>
                    )}
                </p>
                <p style={{
                    margin: '12px 0 0', fontSize: '13px', color: '#64748b', lineHeight: 1.55
                }}>
                    {t('platformBilling:activeStudentHint')}
                </p>
                <button type="button" onClick={() => setShowStudents(true)} style={{ ...btnSecondary, marginTop: '12px' }}>
                    {t('platformBilling:viewActiveStudents')}
                </button>
            </section>

            <section style={card}>
                <h3 style={h3}>{t('platformBilling:schools')}</h3>
                {plan.maxSchools < 999999 && (
                    <div style={{ background: '#e5e7eb', borderRadius: '8px', height: '12px', overflow: 'hidden', marginBottom: '8px' }}>
                        <div style={{ width: `${schoolsPct}%`, height: '100%', background: '#4f46e5' }} />
                    </div>
                )}
                <p>{t('platformBilling:schoolsCount', {
                    count: plan.schoolsCount,
                    max: plan.maxSchools >= 999999 ? '∞' : plan.maxSchools
                })}</p>
                {plan.schoolsCount < plan.maxSchools || plan.maxSchools >= 999999 ? (
                    <Link to="/school" style={{ color: '#4f46e5', fontWeight: 600 }}>{t('platformBilling:addSchool')}</Link>
                ) : (
                    <Link to="/billing/plans" style={btn}>{t('platformBilling:upgradePlan')}</Link>
                )}
            </section>

            <section style={card}>
                <h3 style={h3}>{t('platformBilling:extraCharges')}</h3>
                {charges.length === 0 ? (
                    <p style={{ color: '#888' }}>{t('platformBilling:noExtraCharges')}</p>
                ) : (
                    <table style={{ width: '100%', fontSize: '14px', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ color: '#888', textAlign: 'left' }}>
                                <th style={th}>{t('platformBilling:colMonth')}</th>
                                <th style={th}>{t('platformBilling:colExtra')}</th>
                                <th style={th}>{t('platformBilling:colAmount')}</th>
                                <th style={th}>{t('platformBilling:colStatus')}</th>
                                <th style={th}>{t('platformBilling:colPayment')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {charges.map((c) => {
                                const cs = CHARGE_STATUS[c.status] || CHARGE_STATUS.pending;
                                return (
                                    <tr key={c.id} style={{ borderTop: '1px solid #eee' }}>
                                        <td style={td}>{formatMonth(c.billingPeriodStart)}</td>
                                        <td style={td}>{c.extraStudents}</td>
                                        <td style={td}>{fmt(c.totalAmount)} ₽</td>
                                        <td style={{ ...td, color: cs.color, fontWeight: 600 }}>{cs.label}</td>
                                        <td style={td}>{c.paidAt ? formatDate(c.paidAt) : '—'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </section>

            <section style={card}>
                <h3 style={h3}>{t('platformBilling:paymentHistory')}</h3>
                {historyPreview.length === 0 ? (
                    <p style={{ color: '#888' }}>{t('platformBilling:noPaymentHistory')}</p>
                ) : (
                    <table style={{ width: '100%', fontSize: '14px', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ color: '#888', textAlign: 'left' }}>
                                <th style={th}>{t('platformBilling:colPaymentDate')}</th>
                                <th style={th}>{t('platformBilling:colAmount')}</th>
                                <th style={th}>{t('platformBilling:colStatus')}</th>
                                <th style={th}>{t('platformBilling:colDescription')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {historyPreview.map((p) => {
                                const ps = PAYMENT_STATUS[p.status] || PAYMENT_STATUS.pending;
                                return (
                                    <tr key={p.id} style={{ borderTop: '1px solid #eee' }}>
                                        <td style={td}>{formatDate(p.confirmedAt || p.createdAt)}</td>
                                        <td style={td}>{fmt(p.amount)} ₽</td>
                                        <td style={{ ...td, color: ps.color, fontWeight: 600 }}>{ps.label}</td>
                                        <td style={td}>{p.description || '—'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
                {history.length > 0 && (
                    <button
                        type="button"
                        onClick={() => setShowPaymentHistory(true)}
                        style={{ ...btnSecondary, marginTop: '12px' }}
                    >
                        {t('platformBilling:viewAllPayments')}
                    </button>
                )}
            </section>

            <ActiveStudentsModal open={showStudents} onClose={() => { setShowStudents(false); load(); }} />
            <PaymentHistoryModal
                open={showPaymentHistory}
                onClose={() => setShowPaymentHistory(false)}
                preview={history}
            />
            <PlanChangeModal
                open={showPlanChange}
                onClose={() => setShowPlanChange(false)}
                currentPlan={plan.plan}
                onSuccess={() => { setShowPlanChange(false); load(); }}
            />
        </div>
    );
}

const card = { background: '#fff', borderRadius: '16px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' };
const h3 = { margin: '0 0 12px', fontSize: '18px' };
const btn = { padding: '10px 16px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: 600, cursor: 'pointer', display: 'inline-block' };
const btnSecondary = { ...btn, background: '#fff', color: '#4f46e5', border: '1px solid #4f46e5' };
const th = { padding: '8px 4px' };
const td = { padding: '10px 4px' };
